if (!customElements.get('card-variant-selector')) {
  customElements.define(
    'card-variant-selector',
    class CardVariantSelector extends HTMLElement {
      connectedCallback() {
        this.variants     = JSON.parse(this.querySelector('[type="application/json"]').textContent);
        this.variantInput = this.querySelector('.card-variant-selector__variant-id');
        this.atcBtn       = this.querySelector('.card-variant-selector__atc');
        this.moneyFormat  = this.dataset.moneyFormat;

        // Price element lives in the sibling card info div — scope to card__container
        this.priceEl = this.closest('.card__container')?.querySelector('.price');

        // Primary card image only — excludes .card__img--hover so hover behaviour is unaffected
        this.cardImg     = this.closest('.card__container')?.querySelector('.card__img:not(.card__img--hover)');
        this.defaultImage = this.dataset.defaultImage;

        // Prefetch all variant images in the background so first-click swaps are instant
        this.variants.forEach(v => {
          if (v.featured_image?.src) {
            const img = new Image();
            img.src = this.shopifyImageSrc(v.featured_image.src, 1000);
          }
        });

        this.addEventListener('change', () => this.updateVariant());
      }

      formatMoney(cents) {
        const amount = (cents / 100).toFixed(2);
        return (this.moneyFormat || '${{amount}}')
          .replace('{{amount}}', amount)
          .replace('{{amount_no_decimals}}', Math.round(cents / 100))
          .replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
      }

      getSelectedOptions() {
        return [...this.querySelectorAll('.card-variant-selector__group')].map(group =>
          group.querySelector('input[type="radio"]:checked')?.value
        );
      }

      // Converts a raw Shopify CDN URL to a sized variant
      // e.g. ".../image.jpg" → ".../image_600x.jpg"
      shopifyImageSrc(src, width) {
        return src.replace(/(\.[a-z]+)(\?.*)?$/i, `_${width}x$1$2`);
      }

      updateImage(variant) {
        if (!this.cardImg) return;

        const fi = variant.featured_image;

        // No assigned variant image → restore product default
        const newSrc = fi
          ? this.shopifyImageSrc(fi.src, 1000)
          : this.defaultImage;

        if (!newSrc) return;

        // Avoid a redundant swap if the image is already correct
        const currentSrc = this.cardImg.getAttribute('src') || '';
        if (currentSrc === newSrc) return;

        this.cardImg.src = newSrc;

        if (fi) {
          // Rebuild srcset at the same breakpoints card.liquid uses
          this.cardImg.srcset = [
            fi.width >= 375  ? `${this.shopifyImageSrc(fi.src, 375)} 375w`   : '',
            fi.width >= 750  ? `${this.shopifyImageSrc(fi.src, 750)} 750w`   : '',
            fi.width >= 1000 ? `${this.shopifyImageSrc(fi.src, 1000)} 1000w` : '',
            fi.width >= 1440 ? `${this.shopifyImageSrc(fi.src, 1440)} 1440w` : '',
          ].filter(Boolean).join(', ');

          if (fi.alt) this.cardImg.alt = fi.alt;
        }
      }

      updateVariant() {
        const selected = this.getSelectedOptions();
        const variant = this.variants.find(v =>
          v.options.every((opt, i) => opt === selected[i])
        );
        if (!variant) return;

        // — Update hidden variant ID —
        this.variantInput.value = variant.id;

        // — Update ATC button state —
        const span = this.atcBtn.querySelector('span');
        if (variant.available) {
          this.atcBtn.removeAttribute('disabled');
          span.textContent = window.variantStrings?.addToCart || 'Add to cart';
        } else {
          this.atcBtn.setAttribute('disabled', '');
          span.textContent = window.variantStrings?.soldOut || 'Sold out';
        }

        // — Update price display —
        if (this.priceEl) {
          const regularEl = this.priceEl.querySelector('.wt-product__price__final');
          const compareEl = this.priceEl.querySelector('.wt-product__price__compare');
          const onSale    = variant.compare_at_price > variant.price;

          if (regularEl) regularEl.textContent = this.formatMoney(variant.price);

          this.priceEl.classList.toggle('price--on-sale', onSale);
          this.priceEl.classList.toggle('price--sold-out', !variant.available);

          if (compareEl) {
            compareEl.textContent = onSale ? this.formatMoney(variant.compare_at_price) : '';
            compareEl.classList.toggle('hidden', !onSale);
          }
        }

        // — Update card image (primary only; hover image untouched) —
        this.updateImage(variant);
      }
    }
  );
}
